import { inject, Injectable } from '@angular/core';
import {
  AmbassadorState,
  ConfigurationState,
  DateUtilsService,
  InventoryManagementDateRange,
  InventoryManagementSearchCriteria,
  InventoryManagementState,
  LaunchDarklyService,
  StatusFilter,
  UpdateCurrentStatus,
  UpdateSearchRequest
} from 'remarketing-angular-library';
import { selectedGroupMemberUuids } from '../lead-seller-group/reducer';
import { select, Store } from '@ngxs/store';
import { LeadSellerGroupService } from '../lead-seller-group/lead-seller-group.service';
import { SearchFilterService } from '../search-filter/search-filter.service';
import { pick } from 'lodash';
import { SearchFilterFactory } from '../../factories/search-filter.factory';

enum ActionSelection {
  Vin = 'VIN',
  Stock = 'STOCK'
}

@Injectable({
  providedIn: 'root'
})
export class HeaderSearchExecutionService {
  private readonly store: Store;
  private readonly launchDarklyService: LaunchDarklyService;
  private readonly leadSellerGroupService: LeadSellerGroupService;
  private readonly searchFilterService: SearchFilterService;
  private readonly dateUtilsService: DateUtilsService;
  private readonly searchFilterFactory: SearchFilterFactory;

  private readonly inventoryManagementSearchRequest$ = select(InventoryManagementState.searchRequest);
  private readonly inventoryManagementSearchCriteria$ = select(InventoryManagementState.searchCriteria);
  private readonly inventoryManagementCurrentStatus$ = select(InventoryManagementState.currentStatus);
  protected readonly mobileSearchMode$ = select(InventoryManagementState.mobileSearchMode);

  constructor() {
    this.store = inject(Store);
    this.launchDarklyService = inject(LaunchDarklyService);
    this.dateUtilsService = inject(DateUtilsService);
    this.leadSellerGroupService = inject(LeadSellerGroupService);
    this.searchFilterService = inject(SearchFilterService);
    this.searchFilterFactory = inject(SearchFilterFactory);
  }

  executeCommonSearchBarTextInputSearch(
    vins: string[],
    stockNumbers: string[],
    sellerLegacyId: number,
    isDesktop: boolean
  ): void {
    const vinsForPayload = this.cleanStringArray(vins);
    const stockNumbersForPayload = this.cleanStringArray(stockNumbers);
    const sellerLegacyIdForPayload = sellerLegacyId ? sellerLegacyId : null;

    const updatedCriteriaFromCommonRules = this.buildSearchPayloadWithCommonRules(
      vinsForPayload,
      stockNumbersForPayload,
      sellerLegacyIdForPayload,
      isDesktop
    );

    this.buildFinalCriteriaForVinStockSearchAndUpdateState(updatedCriteriaFromCommonRules);
  }

  buildSearchPayloadWithCommonRules(
    vins: string[],
    stockNumbers: string[],
    sellerLegacyId: number,
    isDesktop: boolean
  ): InventoryManagementSearchCriteria {
    const grounded = undefined;
    const reservedForDealers = [];
    const reservedByUsers = [];
    const dealerGroupState = this.leadSellerGroupService.snapShot();
    const leadSellerGroupMembers = this.isAdmin ? [] : selectedGroupMemberUuids(dealerGroupState);
    const dealerUuid = this.store.selectSnapshot(AmbassadorState.selectedAmbassadorDealershipUuid);

    const statusFilter = this.inventoryManagementCurrentStatus$();

    const filterByStatus = this.isSellUpdateInventoryEnabled
      ? null
      : this.inventoryManagementSearchCriteria$().filterByStatus;

    const dateRange = this.getDateRangeForSearchMode(statusFilter);

    return {
      ...this.inventoryManagementSearchCriteria$(),
      vins,
      stockNumbers,
      sellerLegacyId,
      grounded,
      reservedForDealers,
      reservedByUsers,
      leadSellerGroupMembers,
      dealerUuid,
      filterByStatus,
      dateRange
    } as InventoryManagementSearchCriteria;
  }

  buildFinalCriteriaForVinStockSearchAndUpdateState(updatedSearchCriteria: InventoryManagementSearchCriteria): void {
    const desiredPage = 0;
    const defaultFilterSelectionAfterSearch = this.isSellUpdateInventoryEnabled
      ? StatusFilter.ALL_VEHICLES
      : this.inventoryManagementCurrentStatus$();
    const defaultFilterValues = this.searchFilterFactory.buildDefaultFilter(defaultFilterSelectionAfterSearch);
    const userSpecificValues = pick(updatedSearchCriteria, persistingUserSpecificPropertyNames);
    const leftRailValuesToPersist = pick(updatedSearchCriteria, persistingLeftRailPropertyNames);

    const searchCriteriaForPayload = {
      ...defaultFilterValues,
      ...userSpecificValues,
      ...leftRailValuesToPersist
    } as InventoryManagementSearchCriteria;

    // Set filterByStatus to ALL via LD flag
    if (this.isSellUpdateInventoryEnabled) {
      searchCriteriaForPayload.filterByStatus = null;
      this.store.dispatch(new UpdateCurrentStatus(StatusFilter.ALL_VEHICLES));
    }

    this.updateSearchRequestInStore(searchCriteriaForPayload, desiredPage);
  }

  executeSearchOnLeadSellerGroupMemberChange(selectedGroupMemberOeUuids: string[]): void {
    const updatedSearchCriteria = {
      ...this.inventoryManagementSearchCriteria$(),
      leadSellerGroupMembers: selectedGroupMemberOeUuids
    } as InventoryManagementSearchCriteria;

    this.updateSearchRequestInStore(updatedSearchCriteria, this.inventoryManagementSearchRequest$().page);
  }

  private updateSearchRequestInStore(
    searchCriteriaForPayload: InventoryManagementSearchCriteria,
    desiredPage: number
  ): void {
    // Attention: This update initiates the search
    this.store.dispatch(
      new UpdateSearchRequest({
        ...this.inventoryManagementSearchRequest$(),
        searchCriteria: searchCriteriaForPayload,
        page: desiredPage
      })
    );
  }

  get isSellUpdateInventoryEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-updateInventoryEnabled', false);
  }

  get isSellerLegacyIdSearchEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('sell-sellerLegacyIdSearchEnabled', false);
  }

  get isAdmin(): boolean {
    return this.store.selectSnapshot(ConfigurationState.isAdmin());
  }

  getDateRangeForSearchMode(statusFilter: StatusFilter): InventoryManagementDateRange {
    let defaultFromDate = undefined;
    let defaultToDate = undefined;

    if (this.isSellUpdateInventoryEnabled) {
      defaultToDate = this.dateUtilsService.DEFAULT_DATES.toDate;
      defaultFromDate = this.dateUtilsService.DEFAULT_DATES.allVehiclesFromDate;
    } else {
      switch (statusFilter) {
        case StatusFilter.SOLD_VEHICLES:
          // default to search the last 365 days when doing a VIN SOLD Search
          defaultFromDate = this.dateUtilsService.DEFAULT_DATES.allVehiclesFromDate;
          defaultToDate = this.dateUtilsService.DEFAULT_DATES.today;
          break;
        case StatusFilter.REMOVED_VEHICLES:
          defaultFromDate = this.dateUtilsService.DEFAULT_DATES.removedFromDate;
          defaultToDate = this.dateUtilsService.DEFAULT_DATES.toDate;
          break;
        case StatusFilter.ALL_VEHICLES:
          defaultToDate = this.dateUtilsService.DEFAULT_DATES.toDate;
          defaultFromDate = this.dateUtilsService.DEFAULT_DATES.allVehiclesFromDate;
          break;
      }
    }

    return { fromDate: defaultFromDate, toDate: defaultToDate } as InventoryManagementDateRange;
  }

  private cleanStringArray(arrayToBeCleaned: string[]): string[] {
    if (arrayToBeCleaned?.length) {
      return arrayToBeCleaned.filter(item => {
        if (item === null || item === undefined) {
          return false;
        }

        if (typeof item === 'string') {
          return item.trim() !== '';
        }

        return true;
      });
    } else {
      return [];
    }
  }

  setEmployeeFilter(employees: string[]): void {

    /*
      since the offerLists search criteria object is shared between account manager and normal shared vehicles field,
      we reset this value when the employee filter goes for 0/undefined to n or n to 0/undefined
    */
    let offerLists = this.inventoryManagementSearchCriteria$().offerLists;
    if (
      (!this.inventoryManagementSearchCriteria$().filterByEmployee && employees?.length > 0)
      || (this.inventoryManagementSearchCriteria$().filterByEmployee && !employees)
    ) {
      offerLists = []
    }

    const updatedSearchCriteria = {
      ...this.inventoryManagementSearchCriteria$(),
      filterByEmployee: employees,
      filterByEmployeeDealerMap: this.searchFilterService.resetEmployeeDealerMap(employees),
      offerLists
    } as InventoryManagementSearchCriteria;

    this.updateSearchRequestInStore(updatedSearchCriteria, this.inventoryManagementSearchRequest$().page);
  }
  
}

export const persistingUserSpecificPropertyNames = [
  'vins',
  'stockNumbers',
  'sellerLegacyId',
  'leadSellerGroupMembers',
  'dealerUuid',
  'reservedForDealers',
  'reservedByUsers'
];

export const persistingLeftRailPropertyNames = [
  'sortBy',
  'sortOrder',
  'dateRange',
  'filterByInService',
  'filterByStatus',
  'grounded',
  'tenantUuid'
];
